import React, { memo, useEffect, useState, Fragment } from "react";
import { Card, Col, Form, Row, Select, Table, Button, Modal, Input, Divider, Tooltip, Popconfirm, Switch } from "antd";
import { DeleteOutlined, DownloadOutlined, EditOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import { getGroup } from "../../../redux/actions/group.action";
import { getFacility } from "../../../redux/actions/facility.action";
import { getCommunicationLogin } from "../../../redux/actions/getCommunicationLogin.action";
import { AddCommunicationLogin } from "../../../redux/actions/postCommunicationLogin.action";
import { UpdateCommunicationLogin } from "../../../redux/actions/updateCommunicationLogin.action";
import { deleteCommunicationLogin } from "../../../redux/actions/deleteCommunicationLogin.action";
import { getApiArrayDetails, getUserType, handleTableSort, isValidArray, spaceValidation } from "../../../utils/MyMUtils";
import { PASSWORD_HINT, PASSWORD_MAX, confirmPasswordRules, passwordRules } from "utils/passwordRules";

const { Option } = Select;

export const CommunicationManagement = () => {
  const fileType = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
  const fileExtension = ".xlsx";

  const isFacilityUser = getUserType() === "F";

  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [addForm] = Form.useForm();
  const [editForm] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [editVisible, setEditVisible] = useState(false);
  const [loader, setLoader] = useState(false);
  const [editLoader, setEditLoader] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [addModalGroup, setAddModalGroup] = useState(null);
  const [facilityIds, setFacilityIds] = useState([]);
  const [dataValue, setDataValue] = useState([]);
  const [defaultValue, setDefaultValue] = useState({});
  const [editId, setEditId] = useState("");
  const [checked, setChecked] = useState(false);
  const [fileName, setFileName] = useState("Communication Login");

  const groupList = useSelector((state) => getApiArrayDetails(state.GroupList.data));
  const facilitiesList = useSelector((state) => getApiArrayDetails(state.FacilitiesList.data));

  useEffect(() => {
    dispatch(getGroup());
  }, []);

  useEffect(() => {
    addForm.resetFields();
    editForm.resetFields();
    setAddModalGroup(null);
  }, [visible, editVisible]);

  useEffect(() => {
    form.resetFields(["facility_ids"]);
  }, [facilitiesList]);

  const getGroupList = () => {
    if (isValidArray(groupList)) {
      return groupList.map((group) => (
        <Option key={group?.org_id} value={group?.org_id}>
          {group?.org_name}
        </Option>
      ));
    }
  };

  const getFacilityList = () => {
    if (isValidArray(facilitiesList)) {
      return facilitiesList.map((facility) => (
        <Option key={facility?.facility_id} value={facility?.facility_id} label={facility?.facility_name}>
          {facility?.facility_name}
        </Option>
      ));
    }
  };

  const handleGroupChange = (groupId) => {
    setSelectedGroup(groupId);
    setFacilityIds([]);
    setDataValue([]);
    form.setFieldsValue({ facility_ids: [] });
    if (groupId) {
      dispatch(getFacility(groupId));
    }
  };

  const handleFacilityChange = (facilityIds) => {
    setFacilityIds(facilityIds);
    if (facilityIds && facilityIds.length > 0) {
      setLoading(true);
      const postData = { facility_ids: facilityIds };
      dispatch(getCommunicationLogin(postData)).then((record) => {
        if (isValidArray(record.payload.result)) {
          const data = record.payload.result;
          setDataValue(data.filter((item) => item.login_type === "F"));
          toast.success(record.payload.message);
        } else {
          setDataValue([]);
        }
        setLoading(false);
      });
    } else {
      setDataValue([]);
    }
  };

  const exportToCSV = (csvData, fileName) => {
    if (csvData && csvData.length > 0) {
      const ws = XLSX.utils.json_to_sheet(csvData);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, fileName + fileExtension);
    } else {
      toast.error("There is no data to export.");
    }
  };

  const excelData = () => {
    if (isValidArray(dataValue)) {
      return dataValue.map((item) => ({
        "Facility Name": item.facility_name,
        "User Name": item.user_name,
        "Login Type": item.login_type === "F" ? "Facility" : "Staff",
      }));
    }
    return [];
  };

  const refreshData = () => {
    if (facilityIds && facilityIds.length > 0) {
      setLoading(true);
      const postData = { facility_ids: facilityIds };
      dispatch(getCommunicationLogin(postData)).then((record) => {
        if (isValidArray(record.payload.result)) {
          const data = record.payload.result;
          setDataValue(data.filter((item) => item.login_type === "F"));
        } else {
          setDataValue([]);
        }
        setLoading(false);
      });
    }
  };

  const deleteFunction = (elm) => {
    setLoading(true);
    dispatch(deleteCommunicationLogin(elm.id)).then((record) => {
      if (record.payload.success) {
        toast.success(record.payload.message);
        refreshData();
      } else {
        toast.error(record.payload.message);
        setLoading(false);
      }
    });
  };

  const EditFunction = (record) => {
    setEditId(record.id);
    setDefaultValue(record);
    setEditVisible(true);
  };

  const handleAddLogin = (data) => {
    setLoader(true);
    const payload = { ...data, login_type: "F" };
    dispatch(AddCommunicationLogin(payload)).then((record) => {
      if (record.payload.success) {
        toast.success(record.payload.message);
        setVisible(false);
        refreshData();
      } else {
        toast.error(record.payload.message);
      }
      setLoader(false);
    });
  };

  const handleEditLogin = (data) => {
    setEditLoader(true);
    dispatch(UpdateCommunicationLogin(editId, data)).then((record) => {
      if (record.payload.success) {
        toast.success(record.payload.message);
        setEditVisible(false);
        refreshData();
      } else {
        toast.error(record.payload.message);
      }
      setEditLoader(false);
    });
  };

  const columns = [
    {
      title: "Facility",
      dataIndex: "facility_name",
      key: "facility_name",
      width: "25%",
      sorter: (a, b) => handleTableSort(a, b, "facility_name"),
    },
    {
      title: "User Name",
      dataIndex: "user_name",
      key: "user_name",
      width: "25%",
      sorter: (a, b) => handleTableSort(a, b, "user_name"),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      width: "20%",
      render: (_, elm) => (
        <div>
          <span className="mr-3">
            <Tooltip title="Edit" color="rgb(47, 111, 224)">
              <Button onClick={() => EditFunction(elm)}>
                <EditOutlined />
              </Button>
            </Tooltip>
          </span>
          <span className="mr-3">
            <Popconfirm
              title="Are you sure to delete this data?"
              onConfirm={() => deleteFunction(elm)}
              okText="Yes"
              cancelText="No"
            >
              <Tooltip title="Delete" color="rgb(47, 111, 224)">
                <Button>
                  <DeleteOutlined />
                </Button>
              </Tooltip>
            </Popconfirm>
          </span>
        </div>
      ),
    },
  ];

  return (
    <Card>
      <h4 style={{ marginBottom: 16, color: "#2f6fe0" }}>Facility Access</h4>
      <Form layout="horizontal" form={form}>
        <Row gutter={24}>
          <Col flex="auto"></Col>
          <Col flex="0 1 220px">
            <Form.Item>
              <Select onChange={handleGroupChange} placeholder="Select Group" allowClear>
                {getGroupList()}
              </Select>
            </Form.Item>
          </Col>
          <Col flex="0 1 220px">
            <Form.Item name="facility_ids">
              <Select
                mode="multiple"
                onChange={handleFacilityChange}
                optionFilterProp="label"
                placeholder="Select Facility"
                disabled={!selectedGroup}
              >
                {getFacilityList()}
              </Select>
            </Form.Item>
          </Col>
          <Col flex="none">
            <Button
              shape="round"
              onClick={() => exportToCSV(excelData(), fileName)}
              icon={<DownloadOutlined />}
            >
              Excel
            </Button>
          </Col>
          <Col flex="none">
            <Button
              className="style-btn"
              onClick={() => setVisible(true)}
              icon={<PlusCircleOutlined />}
              shape="round"
              size="default"
            >
              Add Login
            </Button>
          </Col>
        </Row>
      </Form>

      <Table
        loading={loading}
        columns={columns}
        dataSource={dataValue}
        rowKey="id"
        onChange={(pagination, filters, sorter, extra) => {
          setTimeout(() => {
            window.scrollTo({
              top: 0,
              behavior: "smooth",
            });
          });
        }}
      />

      <Modal
        title="Add Login"
        centered
        visible={visible}
        footer={null}
        onCancel={() => setVisible(false)}
        width={600}
      >
        <Form onFinish={handleAddLogin} layout="vertical" form={addForm}>
          <Row gutter={24}>
            <Col md={24}>
              <Form.Item
                name="group_id"
                label="Group"
                rules={[{ required: true, message: "Please select the group." }]}
              >
                <Select
                  placeholder="Select Group"
                  allowClear
                  onChange={(groupId) => {
                    setAddModalGroup(groupId || null);
                    addForm.setFieldsValue({ facility_id: undefined });
                    if (groupId) dispatch(getFacility(groupId));
                  }}
                >
                  {getGroupList()}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={24}>
              <Form.Item
                name="facility_id"
                label="Facility"
                rules={[{ required: true, message: "Please select the facility." }]}
              >
                <Select
                  allowClear
                  placeholder="Select Facility"
                  disabled={!addModalGroup}
                >
                  {getFacilityList()}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Divider>Login Access</Divider>
          <Row gutter={24}>
            <Col md={12}>
              <Form.Item
                name="user_name"
                label="Login ID"
                rules={[
                  { required: true, message: "Please enter the login ID." },
                  spaceValidation("Login ID cannot be empty."),
                ]}
              >
                <Input placeholder="Login ID" />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="password" label="Password" extra={PASSWORD_HINT} rules={passwordRules()}>
                <Input.Password placeholder="Password" maxLength={PASSWORD_MAX} />
              </Form.Item>
            </Col>
            <Col md={12}>
              <Form.Item name="confirm_password" label="Confirm Password" dependencies={["password"]} rules={confirmPasswordRules("password")}>
                <Input.Password placeholder="Confirm password" maxLength={PASSWORD_MAX} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col md={24} style={{ textAlign: "center" }}>
              <Button htmlType="submit" loading={loader}>
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>

      <Modal
        title="Edit Login"
        centered
        visible={editVisible}
        footer={null}
        onCancel={() => setEditVisible(false)}
        width={400}
      >
        <Form onFinish={handleEditLogin} layout="vertical" form={editForm}>
          <Row gutter={24}>
            <Col md={24}>
              <Form.Item
                name="user_name"
                label="Username"
                initialValue={defaultValue.user_name}
                rules={[
                  { required: true, message: "Please enter the user name." },
                  spaceValidation("User Name cannot be empty."),
                ]}
              >
                <Input placeholder="Enter User Name" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={24}>
            <Col lg={24} md={24} sm={24} xs={24}>
              <Form.Item label="New Password">
                <Switch onChange={setChecked} />
              </Form.Item>
            </Col>
            {checked && (
              <Col lg={24} md={24} sm={24} xs={24}>
                <Form.Item className="mt-3" label="Password" name="password" extra={PASSWORD_HINT} rules={passwordRules()}>
                  <Input.Password placeholder="Enter Password" maxLength={PASSWORD_MAX} />
                </Form.Item>
                <Form.Item name="confirm_password" label="Confirm Password" dependencies={["password"]} rules={confirmPasswordRules("password")}>
                  <Input.Password placeholder="Confirm password" maxLength={PASSWORD_MAX} />
                </Form.Item>
              </Col>
            )}
          </Row>
          <Row gutter={24}>
            <Col md={24} style={{ textAlign: "center" }}>
              <Button htmlType="submit" loading={editLoader}>
                Submit
              </Button>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
};

export default memo(CommunicationManagement);
